import { useEffect, useState, useRef, useLayoutEffect } from 'react';
import { auth, db } from '../firebase';
import { collection, query, where, onSnapshot, getDoc, doc } from 'firebase/firestore';
import { Link } from 'react-router-dom';
import { useReturnViewState, useRestoreScroll, TRADES_FLOW_PREFIXES, ACCEPTED_TRADES_VIEW_KEY } from '../hooks/useReturnViewState';
import Footer from '../components/Footer';
import { useNotifications } from '../contexts/NotificationContext';
import { getInitiatorPedalIds } from '../utils/tradeUtils';
import { placeholderFor } from '../utils/listingPlaceholder';

const getUserTradeStep = (trade) => {
  const shipping = trade.shippingStatus || {};

  const fromUser = {
    paid: trade.paymentStatus?.fromUserPaid || false,
    photoUploaded: shipping.fromUserPhotos?.length > 0,
    ready: shipping.fromUserReady || false,
    label: shipping.fromUser?.labelPurchased || false,
    inTransit: shipping.fromUser?.inTransit || shipping.fromUser?.delivered || false,
    delivered: shipping.fromUser?.delivered || false,
  };

  const toUser = {
    paid: trade.paymentStatus?.toUserPaid || false,
    photoUploaded: shipping.toUserPhotos?.length > 0,
    ready: shipping.toUserReady || false,
    label: shipping.toUser?.labelPurchased || false,
    inTransit: shipping.toUser?.inTransit || shipping.toUser?.delivered || false,
    delivered: shipping.toUser?.delivered || false,
  };

  const steps = [
    { label: 'Payment Completed', check: (u) => u.paid },
    { label: 'Confirmation Photos Uploaded', check: (u) => u.photoUploaded },
    { label: 'Ready to Ship', check: (u) => u.ready },
    { label: 'Shipping Label Generated', check: (u) => u.label },
    { label: 'Item In Transit', check: (u) => u.inTransit },
    { label: 'Item Delivered', check: (u) => u.delivered },
    { label: 'Feedback Left', check: () => false },
  ];

  let lastCompleted = 'Trade Accepted';
  for (let i = 0; i < steps.length; i++) {
    if (steps[i].check(fromUser) && steps[i].check(toUser)) {
      lastCompleted = steps[i].label;
    } else {
      break;
    }
  }
  return lastCompleted;
};


const AcceptedTrades = () => {
  const { notifications, markAsRead } = useNotifications();
  const [acceptedTrades, setAcceptedTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  // "Put me back where I was": same mechanism as Offers/Offer History (see
  // hooks/useReturnViewState.js). The save fires from a capture handler on
  // the page wrapper rather than per-link onClick: this page has a dozen
  // outbound links and per-link wiring would drift. Harmless extra saves are
  // overwritten; the snapshot is only consumed on an in-flow return.
  const { scrollSnapshot, saveView } = useReturnViewState({
    storageKey: ACCEPTED_TRADES_VIEW_KEY,
    flowPrefixes: TRADES_FLOW_PREFIXES,
  });
  useRestoreScroll(scrollSnapshot, !loading);
  const [usernames, setUsernames] = useState({});
  const [currentUser, setCurrentUser] = useState(null);
  const [notLoggedIn, setNotLoggedIn] = useState(false);
  const [unreadCounts, setUnreadCounts] = useState({});
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [isHeaderCompact, setIsHeaderCompact] = useState(() => {
    if (typeof window === 'undefined') return false;
    if (window.innerWidth <= 768) return false;
    return window.scrollY > 400;
  });
  const [showMobileChromeBar, setShowMobileChromeBar] = useState(false);
  const [mobileChromeBarHeight, setMobileChromeBarHeight] = useState(0);
  const mobileChromeInflowRef = useRef(null);
  const mobileChromeSlotRef = useRef(null);
  const mobileChromeBarHeightRef = useRef(0);
  const navbarHeightRef = useRef(125);
  const lastScrollYRef = useRef(typeof window !== 'undefined' ? window.scrollY : 0);
  const mobileChromeHandedOffRef = useRef(false);

  useLayoutEffect(() => {
    const readNavbarHeight = () => {
      const v = getComputedStyle(document.documentElement).getPropertyValue('--navbar-height').trim();
      navbarHeightRef.current = v ? parseFloat(v) : 125;
    };
    readNavbarHeight();
    window.addEventListener('resize', readNavbarHeight);
    return () => window.removeEventListener('resize', readNavbarHeight);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const y = window.scrollY;
      setShowBackToTop(y > 300);
      const isMobile = window.innerWidth <= 768;
      if (isMobile) {
        const lastY = lastScrollYRef.current;
        const delta = y - lastY;
        const chromeSlotBottom = navbarHeightRef.current + mobileChromeBarHeightRef.current;
        const pastChromeSlot = y > chromeSlotBottom;
        const slotTop = mobileChromeSlotRef.current?.getBoundingClientRect().top ?? -1;

        if (y > chromeSlotBottom + 30) {
          mobileChromeHandedOffRef.current = false;
        }

        if (slotTop >= 0 || y <= 10) {
          setShowMobileChromeBar(false);
          if (slotTop >= 0) mobileChromeHandedOffRef.current = true;
        } else if (delta > 5) {
          setShowMobileChromeBar(false);
        } else if (delta < -5 && pastChromeSlot && !mobileChromeHandedOffRef.current) {
          setShowMobileChromeBar(true);
        }
        lastScrollYRef.current = y;
      } else {
        setIsHeaderCompact(y > 400);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useLayoutEffect(() => {
    const el = mobileChromeInflowRef.current;
    if (!el) return;
    const measure = () => {
      const h = el.offsetHeight;
      if (h > 0) {
        mobileChromeBarHeightRef.current = h;
        setMobileChromeBarHeight(h);
      }
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [loading, notLoggedIn]);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const headerLinkBtnStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '0.5rem 1rem',
    borderRadius: '9999px',
    border: '1px solid #C89F3C',
    backgroundColor: 'white',
    color: '#374151',
    fontFamily: 'Nunito, sans-serif',
    fontWeight: 700,
    fontSize: '0.875rem',
    textDecoration: 'none',
    whiteSpace: 'nowrap',
    flexShrink: 0,
  };

  const renderMobileChromeBar = () => (
    <div className="at-compact-bar-inner">
      <span className="at-compact-title">Accepted Trades</span>
      <span className="at-compact-spacer" aria-hidden="true" />
      <Link to="/completed-trades" className="at-link-btn" style={headerLinkBtnStyle}>
        Completed Trades
      </Link>
    </div>
  );

  // Mark trade-related notifications as read when viewing accepted trades
  useEffect(() => {
    const markTradeNotificationsRead = async () => {
      const tradeNotifications = notifications.filter(
        n => ((n.type === 'offer_status_change' && n.metadata?.action === 'accepted') || 
              n.type === 'ready_to_ship' || 
              n.type === 'tracking_added_trade') && 
             !n.read
      );
      
      if (tradeNotifications.length > 0) {
        console.log(`📧 Marking ${tradeNotifications.length} trade notification(s) as read on AcceptedTrades page`);
        for (const notification of tradeNotifications) {
          await markAsRead(notification.id);
        }
      }
    };

    if (notifications.length > 0) {
      markTradeNotificationsRead();
    }
  }, [notifications, markAsRead]);

  const requestedUserIdsRef = useRef(new Set());
  const fetchUsernames = async (tradesList) => {
    // Get both fromUserId and toUserId from all trades
    const fromUserIds = tradesList.map(t => t.fromUserId).filter(Boolean);
    const toUserIds = tradesList.map(t => t.toUserId).filter(Boolean);
    
    // Also include current user ID to fetch their username
    const currentUserId = auth.currentUser?.uid;
    const allUserIds = [...fromUserIds, ...toUserIds];
    if (currentUserId && !allUserIds.includes(currentUserId)) {
      allUserIds.push(currentUserId);
    }
    
    const uniqueUserIds = [...new Set(allUserIds)];
    console.log('🔍 AcceptedTrades - Fetching usernames for:', uniqueUserIds);
    const newUsernames = {};

    // Parallel, and each profile fetched once per page life: same treatment
    // as the Offers hub (8f972cf). The old serial loop's "already fetched?"
    // check read stale closure state, so every live-listener fire re-fetched
    // every profile one at a time.
    const toFetch = uniqueUserIds.filter((uid) => !requestedUserIdsRef.current.has(uid));
    if (toFetch.length === 0) return;
    toFetch.forEach((uid) => requestedUserIdsRef.current.add(uid));

    const results = await Promise.all(toFetch.map(async (uid) => {
      try {
        const userDoc = await getDoc(doc(db, 'publicProfiles', uid));
        return [uid, userDoc.exists() ? userDoc.data().username || 'Unknown' : 'Unknown'];
      } catch (err) {
        console.error('Error fetching username for', uid, err);
        requestedUserIdsRef.current.delete(uid); // let a later update retry
        return [uid, 'Unknown'];
      }
    }));

    Object.assign(newUsernames, Object.fromEntries(results));
    setUsernames((prev) => ({...prev, ...newUsernames}));
  };

  useEffect(() => {
  let unsubscribeInitiator;
  let unsubscribeResponder;
  let unsubscribeAuth;

  unsubscribeAuth = auth.onAuthStateChanged((user) => {
    setCurrentUser(user);
    if (!user) { setNotLoggedIn(true); setLoading(false); return; }

    const userId = user.uid;

    // We need to query for trades where user is either initiator or responder
    // Since Firestore doesn't support OR queries, we'll query for initiator trades
    const initiatorQuery = query(
      collection(db, 'tradeOffers'),
      where('status', 'in', ['accepted', 'inProgress', 'payment_completed']),
      where('fromUserId', '==', userId)
    );
    
    const responderQuery = query(
      collection(db, 'tradeOffers'),
      where('status', 'in', ['accepted', 'inProgress', 'payment_completed']),
      where('toUserId', '==', userId)
    );

    // Listen to both queries and combine results
    unsubscribeInitiator = onSnapshot(initiatorQuery, async (initiatorSnapshot) => {
      unsubscribeResponder = onSnapshot(responderQuery, async (responderSnapshot) => {
        console.log('🔍 AcceptedTrades - Initiator trades:', initiatorSnapshot.docs.length);
        console.log('🔍 AcceptedTrades - Responder trades:', responderSnapshot.docs.length);
        
        // Combine both snapshots
        const allDocs = [...initiatorSnapshot.docs, ...responderSnapshot.docs];
        console.log('🔍 AcceptedTrades - Total trades:', allDocs.length);
        
        const trades = await Promise.all(allDocs.map(async docSnap => {
        const trade = { id: docSnap.id, ...docSnap.data() };
        console.log('🔍 AcceptedTrades - Processing trade:', { 
          id: trade.id, 
          status: trade.status, 
          fromUserId: trade.fromUserId, 
          toUserId: trade.toUserId,
          paymentStatus: trade.paymentStatus
        });

        let initiatorPedals = [];
        let responderPedal = null;

        if (trade.pedalSnapshots?.initiatorPedal && trade.pedalSnapshots?.responderPedal) {
          console.log('📸 Using pedal snapshots for accepted trade display');
          initiatorPedals = (trade.pedalSnapshots.initiatorPedals && Array.isArray(trade.pedalSnapshots.initiatorPedals))
            ? trade.pedalSnapshots.initiatorPedals
            : [trade.pedalSnapshots.initiatorPedal];
          responderPedal = trade.pedalSnapshots.responderPedal;
        } else {
          console.log('🔄 Fetching live pedal data for accepted trade');
          const allInitiatorIds = getInitiatorPedalIds(trade);
          const initiatorSnaps = await Promise.all(
            allInitiatorIds.map(pid => getDoc(doc(db, 'listings', pid)))
          );
          initiatorPedals = initiatorSnaps
            .filter(s => s.exists())
            .map(s => ({ id: s.id, ...s.data() }));
          const responderPedalSnap = await getDoc(doc(db, 'listings', trade.responderPedalId));
          responderPedal = responderPedalSnap.exists() ? { id: responderPedalSnap.id, ...responderPedalSnap.data() } : null;
        }

        const isInitiator = user?.uid === trade.fromUserId;
        const myPedals = isInitiator ? initiatorPedals : (responderPedal ? [responderPedal] : []);
        const theirPedals = isInitiator ? (responderPedal ? [responderPedal] : []) : initiatorPedals;
        const myPedal = myPedals[0] || null;
        const theirPedal = theirPedals[0] || null;

        console.log('🔍 AcceptedTrades - Pedal data for trade:', {
          id: trade.id,
          isInitiator,
          initiatorPedalsCount: initiatorPedals.length,
          myPedalsCount: myPedals.length,
          theirPedalsCount: theirPedals.length
        });

        return { ...trade, myPedal, theirPedal, myPedals, theirPedals };
      }));

        const userId = auth.currentUser?.uid;
        console.log('🔍 AcceptedTrades - All trades:', trades.map(t => ({ id: t.id, status: t.status, fromUserId: t.fromUserId, toUserId: t.toUserId })));
        console.log('🔍 AcceptedTrades - Current user ID:', userId);
        
        // No need to filter again since queries already filter by user
        const results = trades;
        
        console.log('🔍 AcceptedTrades - Filtered trades for user:', results.map(t => ({ id: t.id, status: t.status })));

        // Sort by acceptance date (from history), fallback to createdAt
        results.sort((a, b) => {
          // Find the acceptance timestamp from history
          const getAcceptanceDate = (trade) => {
            if (trade.history && Array.isArray(trade.history)) {
              // Find the last "accepted" entry in history
              const acceptedEntry = [...trade.history].reverse().find(h => 
                h.message && h.message.toLowerCase().includes('accepted')
              );
              if (acceptedEntry && acceptedEntry.timestamp) {
                return acceptedEntry.timestamp.toDate?.() || new Date(acceptedEntry.timestamp);
              }
            }
            // Fallback to createdAt
            return trade.createdAt?.toDate?.() || new Date(0);
          };
          
          const aDate = getAcceptanceDate(a);
          const bDate = getAcceptanceDate(b);
          return bDate - aDate;
        });

        console.log('✅ Matching accepted trades with pedal data:', results);
        setAcceptedTrades(results);
        setLoading(false);
        
        // Fetch usernames for all users in trades
        if (results.length > 0) {
          fetchUsernames(results);
        }
      });
    });
  });

  return () => {
    if (unsubscribeInitiator) unsubscribeInitiator();
    if (unsubscribeResponder) unsubscribeResponder();
    if (unsubscribeAuth) unsubscribeAuth();
  };
}, []);




  if (notLoggedIn) {
    return (
      <div style={{ minHeight: '50vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem' }}>
        <div style={{ maxWidth: '500px', width: '100%', backgroundColor: '#e8f4fd', border: '2px solid #5A9ECB', borderRadius: '12px', padding: '2rem', textAlign: 'center', fontFamily: 'Nunito, sans-serif' }}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: '700', color: '#1a5276', marginBottom: '1rem' }}>Log In to View Accepted Trades</h2>
          <p style={{ fontSize: '1rem', color: '#2c3e50', lineHeight: '1.6', marginBottom: '1.5rem' }}>You need to be logged in to view your accepted trades.</p>
          <Link to="/login" style={{ display: 'inline-block', backgroundColor: '#5A9ECB', color: 'white', fontWeight: '600', padding: '0.75rem 2rem', border: 'none', borderRadius: '6px', fontSize: '1rem', textDecoration: 'none', fontFamily: 'Nunito, sans-serif' }}>Go to Login</Link>
        </div>
      </div>
    );
  }

  if (loading) return <div className="p-4">Loading accepted trades...</div>;

  return (
    <div className="min-h-screen" onClickCapture={() => saveView('list')} style={{ backgroundColor: 'transparent', minHeight: 'auto', display: 'flex', flexDirection: 'column' }}>
      {/* Yellow background section */}
      <div
        className="accepted-trades-page"
        style={{
          backgroundColor: '#F7F3EB',
          flex: 1,
          flexDirection: 'column',
          padding: '0 1rem',
          display: 'flex',
        }}
      >
      <div className="accepted-trades-page-content" style={{ paddingTop: '2rem', paddingBottom: '2rem', flex: 1 }}>
        <div className={`at-sticky-header ${isHeaderCompact ? 'is-compact' : ''}`}>
          <style>{`
            .at-compact-bar-inner {
              max-width: 1100px;
              margin: 0 auto;
              width: 100%;
              box-sizing: border-box;
              display: flex;
              justify-content: flex-start;
              align-items: center;
              gap: 12px;
            }
            .at-compact-title {
              font-family: Nunito, sans-serif;
              font-weight: 700;
              font-size: 1.125rem;
              color: #111827;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
              min-width: 0;
              flex: 0 0 auto;
              text-align: left;
            }
            .at-compact-spacer {
              flex: 1 1 auto;
              min-width: 0;
            }
            .at-sticky-header .at-link-btn {
              flex-shrink: 0;
            }
            .at-sticky-header .at-header-expanded {
              display: block;
            }
            .at-sticky-header .at-mobile-chrome-slot {
              display: none;
            }
            .at-sticky-header .at-compact-bar-mobile-fixed {
              display: none;
            }
            @media (min-width: 769px) {
              .at-sticky-header .at-compact-bar-mobile-fixed {
                display: none !important;
              }
              .at-sticky-header .at-compact-bar {
                position: fixed;
                top: 0;
                left: 0;
                right: 0;
                z-index: 100;
                padding: 0.6rem 1rem 0.7rem 1rem;
                background-color: #F7F3EB;
                box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
                box-sizing: border-box;
                opacity: 0;
                pointer-events: none;
                transition: opacity 0.2s ease;
              }
              .at-sticky-header.is-compact .at-compact-bar {
                opacity: 1;
                pointer-events: auto;
              }
              .at-sticky-header .at-link-btn {
                padding: 0.6rem 1.4rem !important;
              }
            }
            @media (max-width: 768px) {
              .accepted-trades-page-content {
                padding-top: 0 !important;
              }
              .at-sticky-header .at-header-expanded {
                display: none !important;
              }
              .at-sticky-header .at-mobile-chrome-slot {
                display: block !important;
                position: relative;
                flex-shrink: 0;
                background-color: #F7F3EB;
                overflow: hidden;
                margin-left: -0.75rem;
                margin-right: -0.75rem;
                width: calc(100% + 1.5rem);
              }
              .at-sticky-header .at-compact-bar-inflow {
                display: block;
                padding: 0.6rem 1rem 1.5rem 1rem;
                background-color: #F7F3EB;
                box-sizing: border-box;
              }
              .at-sticky-header .at-compact-bar-inflow.is-suppressed {
                visibility: hidden;
                pointer-events: none;
              }
              .at-sticky-header .at-compact-bar-desktop {
                display: none !important;
              }
              .at-sticky-header .at-compact-bar-mobile-fixed {
                display: block;
              }
              .at-sticky-header .at-compact-bar {
                display: none;
                position: fixed;
                top: 0;
                left: 0;
                right: 0;
                z-index: 100;
                padding: 0.6rem 1rem 0.85rem 1rem;
                background-color: #F7F3EB;
                box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
                box-sizing: border-box;
                pointer-events: none;
                transform: translateY(-100%);
                transition: transform 0.2s ease;
              }
              .at-sticky-header .at-compact-bar.is-mobile-visible {
                display: block;
                transform: translateY(0);
                pointer-events: auto;
              }
              .at-sticky-header .at-compact-title {
                font-size: calc(1rem + 1pt) !important;
                color: #5A9ECB !important;
              }
              .at-sticky-header .at-link-btn {
                padding: 0.5rem 1rem !important;
                font-size: 0.875rem !important;
              }
            }
          `}</style>

          <div
            ref={mobileChromeSlotRef}
            className="at-mobile-chrome-slot"
            style={mobileChromeBarHeight > 0 ? { height: mobileChromeBarHeight, minHeight: mobileChromeBarHeight } : undefined}
          >
            <div
              ref={mobileChromeInflowRef}
              className={['at-compact-bar-inflow', showMobileChromeBar && 'is-suppressed'].filter(Boolean).join(' ')}
            >
              {renderMobileChromeBar()}
            </div>
          </div>

          <div
            className={[
              'at-compact-bar',
              showMobileChromeBar && 'is-mobile-visible',
            ].filter(Boolean).join(' ')}
          >
            <div className="at-compact-bar-mobile-fixed">
              {renderMobileChromeBar()}
            </div>
            <div className="at-compact-bar-desktop">
              {renderMobileChromeBar()}
            </div>
          </div>

          <div className="at-header-expanded">
            <Link
              to="/trading"
              className="accepted-trades-header-back-link"
              style={{
                color: '#5A9ECB',
                textDecoration: 'none',
                alignItems: 'center',
                marginBottom: '1rem',
                fontSize: '1.125rem'
              }}
            >
              ← Back to Trading
            </Link>
            <div className="accepted-trades-header-expanded-inner">
              <div className="accepted-trades-header-copy">
                <h1 className="font-bold text-gray-900" style={{ fontSize: '2.25rem', fontWeight: '700', letterSpacing: '-0.025em' }}>
                  Accepted Trades
                </h1>
                <p className="accepted-trades-header-description text-gray-600" style={{ fontSize: '17px' }}>
                  {acceptedTrades.length} trade{acceptedTrades.length !== 1 ? 's' : ''} • Sorted by most recent
                </p>
              </div>
              <div className="accepted-trades-header-links">
                <Link to="/completed-trades" className="at-link-btn" style={headerLinkBtnStyle}>
                  Completed Trades
                </Link>
              </div>
            </div>
          </div>
        </div>

      <style jsx>{`
        @media (min-width: 769px) {
          .accepted-trades-page {
            padding-left: 0 !important;
            padding-right: 0 !important;
          }
          .accepted-trades-header-back-link {
            max-width: 1100px;
            width: 100%;
            margin-left: auto;
            margin-right: auto;
            padding-left: 2rem;
            padding-right: 2rem;
            box-sizing: border-box;
            display: flex !important;
            align-items: center;
          }
          .accepted-trades-header-expanded-inner {
            max-width: 1100px;
            width: 100%;
            margin-left: auto;
            margin-right: auto;
            padding-left: 2rem;
            padding-right: 2rem;
            box-sizing: border-box;
            display: flex;
            flex-wrap: wrap;
            align-items: flex-start;
            gap: 0.5rem 5rem;
            margin-bottom: 1.5rem;
          }
          .accepted-trades-header-copy {
            flex: 0 1 auto;
            min-width: 0;
          }
          .accepted-trades-header-links {
            flex: 0 0 auto;
            margin-left: auto;
            display: flex;
            flex-direction: column;
            align-items: flex-end;
            gap: 0.5rem;
            align-self: flex-end;
          }
          .accepted-trades-header-expanded-inner h1 {
            text-align: left !important;
            margin: 0 !important;
          }
          .accepted-trades-header-description {
            margin: 0 !important;
            padding: 0 !important;
            text-align: left !important;
          }
          .accepted-trades-list {
            max-width: 1100px !important;
            padding: 0 2rem !important;
            box-sizing: border-box !important;
            align-items: stretch !important;
          }
          .accepted-trades-empty {
            padding: 0 2rem !important;
            box-sizing: border-box !important;
          }
          .accepted-trade-card {
            width: 100% !important;
            max-width: none !important;
          }
        }
        @media (max-width: 768px) {
          .accepted-trades-page {
            padding: 0 0.75rem !important;
          }
          .accepted-trades-list {
            padding: 0 !important;
          }
          .accepted-trades-empty {
            padding: 0 !important;
          }
        }
      `}</style>

    {acceptedTrades.length === 0 ? (
      <p className='text-gray-600 accepted-trades-empty' style={{ maxWidth: '1100px', margin: '0 auto', boxSizing: 'border-box' }}>You have no accepted trades.</p>
    ) : (
      <div className="accepted-trades-list" style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', maxWidth: '1100px', margin: '0 auto', width: '100%', boxSizing: 'border-box' }}>
        {acceptedTrades.map((trade) => {
          const isFromUser = trade.fromUserId === auth.currentUser?.uid;
          const userPaid = isFromUser 
            ? trade.paymentStatus?.fromUserPaid 
            : trade.paymentStatus?.toUserPaid;
          
          // Get acceptance date from history
          const getAcceptanceDate = (trade) => {
            if (trade.history && Array.isArray(trade.history)) {
              const acceptedEntry = [...trade.history].reverse().find(h => 
                h.message && h.message.toLowerCase().includes('accepted')
              );
              if (acceptedEntry && acceptedEntry.timestamp) {
                return acceptedEntry.timestamp.toDate?.() || new Date(acceptedEntry.timestamp);
              }
            }
            return trade.createdAt?.toDate?.() || new Date();
          };
          
          const tradeDate = getAcceptanceDate(trade);
          
          return (
          <div 
            key={trade.id}
            data-restore-anchor={trade.id}
            className="accepted-trade-card"
            style={{ 
              borderRadius: '12px',
              boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
              padding: '1.5rem',
              marginBottom: '1.5rem',
              backgroundColor: 'white',
              width: '100%',
              boxSizing: 'border-box'
            }}
          >
            <style jsx>{`
              @media (min-width: 769px) {
                .accepted-single-pedal-col {
                  display: flex;
                  flex-direction: column;
                  justify-content: center;
                }
              }
              @media (max-width: 768px) {
                .accepted-trade-card {
                  width: 100% !important;
                  padding: 1rem !important;
                  margin: 0 0 1rem 0 !important;
                }
                .accepted-pedal-inner {
                  flex-direction: column !important;
                }
                .accepted-trade-image {
                  width: 100% !important;
                  max-width: 180px !important;
                  height: auto !important;
                  aspect-ratio: 1 !important;
                }
                .accepted-trade-swap-desktop {
                  display: none !important;
                }
                .accepted-trade-mobile-divider {
                  display: block !important;
                }
                .accepted-trade-buttons {
                  justify-content: space-between !important;
                  width: 100% !important;
                }
                .accepted-payment-button {
                  order: 3 !important;
                  width: 100% !important;
                  text-align: center !important;
                  box-sizing: border-box !important;
                }
                .accepted-mobile-username {
                  display: block !important;
                  text-align: left !important;
                }
                .accepted-desktop-username {
                  display: none !important;
                }
                .accepted-trade-header {
                  flex-direction: column !important;
                  align-items: flex-start !important;
                  gap: 0.5rem !important;
                }
                .accepted-trade-header > div {
                  justify-content: flex-start !important;
                }
                .accepted-trade-date-long {
                  display: none !important;
                }
                .accepted-trade-date-short {
                  display: inline !important;
                }
                .accepted-multi-image {
                  width: 52px !important;
                  height: 52px !important;
                }
                .accepted-multi-image {
                  width: 52px !important;
                  height: 52px !important;
                }
              }
            `}</style>

            {/* Top row: Date + Status pills */}
            <div className="accepted-trade-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '0.75rem', borderBottom: '1px solid #e5e7eb' }}>
              <span style={{ fontSize: '0.9rem', color: '#6b7280', fontFamily: 'Nunito', fontWeight: '500', whiteSpace: 'nowrap' }}>
                <span className="accepted-trade-date-long">{tradeDate ? tradeDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : 'Unknown date'}</span>
                <span className="accepted-trade-date-short" style={{ display: 'none' }}>{tradeDate ? tradeDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Unknown date'}</span>
              </span>
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                {trade.cashAmount > 0 && (
                  <span style={{ padding: '3px 10px', borderRadius: '9999px', fontSize: '0.75rem', fontWeight: '600', fontFamily: 'Nunito', border: '1px solid #C9A227', color: '#946F00', backgroundColor: 'rgba(201, 162, 39, 0.08)' }}>
                    ${trade.cashAmount.toFixed(0)} offered
                  </span>
                )}
                <span style={{ padding: '3px 10px', borderRadius: '9999px', fontSize: '0.75rem', fontWeight: '600', fontFamily: 'Nunito', border: userPaid ? '1px solid #a7c4a0' : '1px solid #fca5a5', color: userPaid ? '#5a7d53' : '#991b1b', backgroundColor: userPaid ? 'rgba(90, 125, 83, 0.07)' : 'rgba(153, 27, 27, 0.06)' }}>
                  {userPaid ? 'Paid' : 'Unpaid'}
                </span>
                <span style={{ padding: '3px 10px', borderRadius: '9999px', fontSize: '0.75rem', fontWeight: '600', fontFamily: 'Nunito', border: '1px solid #93c5fd', color: '#1e40af', backgroundColor: 'rgba(30, 64, 175, 0.06)' }}>
                  {getUserTradeStep(trade)}
                </span>
              </div>
            </div>

            {/* Pedals row */}
            <div style={{ display: 'flex', alignItems: 'stretch', padding: '1rem 0' }}>
              {/* My Pedal(s) */}
              <div className={trade.theirPedals && trade.theirPedals.length > 1 ? 'accepted-single-pedal-col' : ''} style={{ flex: 1 }}>
                <div className="accepted-mobile-username" style={{ display: 'none', fontSize: '0.8rem', color: '#6b7280', fontFamily: 'Nunito', marginBottom: '6px' }}>
                  {usernames[auth.currentUser?.uid] || 'Loading...'} (you)
                </div>
                {trade.myPedals && trade.myPedals.length > 1 ? (
                  <div>
                    <div className="accepted-multi-container" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {trade.myPedals.map((pedal, idx) => (
                        <Link
                          key={pedal?.id || idx}
                          className="accepted-multi-link"
                          to={`/archived-listing/trade/${trade.id}/my${pedal?.id ? `/${pedal.id}` : ''}`}
                          style={{ display: 'flex', gap: '10px', alignItems: idx === 0 ? 'flex-start' : 'center', textDecoration: 'none', color: 'inherit' }}
                        >
                          {(pedal?.images?.top || placeholderFor(pedal || {}).url) ? (
                            <img src={pedal?.images?.top || placeholderFor(pedal || {}).url} alt={`${pedal.make} ${pedal.model}`}
                              className="accepted-multi-image"
                              style={{ width: '64px', height: '64px', objectFit: 'cover', borderRadius: '6px', backgroundColor: '#f3f4f6', flexShrink: 0 }} />
                          ) : (
                            <div className="accepted-multi-image" style={{ width: '64px', height: '64px', backgroundColor: '#f3f4f6', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                              <span style={{ fontSize: '0.6rem', color: '#9ca3af' }}>No Image</span>
                            </div>
                          )}
                          <div className="accepted-multi-text" style={{ textAlign: 'left' }}>
                            {idx === 0 && (
                              <div className="accepted-desktop-username" style={{ fontSize: '0.8rem', color: '#6b7280', fontFamily: 'Nunito', marginBottom: '2px' }}>
                                {usernames[auth.currentUser?.uid] || 'Loading...'} (you)
                              </div>
                            )}
                            <span style={{ fontWeight: '700', fontSize: '0.85rem', color: '#5A9ECB', fontFamily: 'Nunito' }}>{pedal?.make}</span>{' '}
                            <span style={{ fontSize: '0.85rem', color: '#5A9ECB', fontFamily: 'Nunito' }}>{pedal?.model}</span>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                ) : (
                <div className="accepted-pedal-inner" style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                  {(trade.myPedal?.images?.top || placeholderFor(trade.myPedal || {}).url) ? (
                    <img
                      src={trade.myPedal?.images?.top || placeholderFor(trade.myPedal || {}).url}
                      alt='your pedal'
                      className="accepted-trade-image"
                      style={{ width: '120px', height: '120px', objectFit: 'cover', borderRadius: '8px', backgroundColor: '#f3f4f6', flexShrink: 0 }}
                    />
                  ) : (
                    <div className="accepted-trade-image" style={{ width: '120px', height: '120px', backgroundColor: '#f3f4f6', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <span style={{ fontSize: '0.7rem', color: '#9ca3af' }}>No Image</span>
                    </div>
                  )}
                  <div style={{ textAlign: 'left' }}>
                    <div className="accepted-desktop-username" style={{ fontSize: '0.8rem', color: '#6b7280', fontFamily: 'Nunito', marginBottom: '2px' }}>
                      {usernames[auth.currentUser?.uid] || 'Loading...'} (you)
                    </div>
                    <div style={{ fontWeight: '700', fontSize: '0.95rem', color: '#1f2937', fontFamily: 'Nunito' }}>{trade.myPedal?.make}</div>
                    <div style={{ fontSize: '0.85rem', color: '#6b7280', fontFamily: 'Nunito' }}>{trade.myPedal?.model}</div>
                    <div style={{ fontSize: '0.8rem', color: '#9ca3af', fontFamily: 'Nunito', marginTop: '2px' }}>
                      {trade.myPedal?.condition} · ${trade.myPedal?.price}
                    </div>
                    {trade.myPedal?.id && (
                      <Link
                        to={`/archived-listing/trade/${trade.id}/my`}
                        style={{ fontSize: '0.8rem', color: '#5A9ECB', textDecoration: 'none', fontFamily: 'Nunito', display: 'inline-block', marginTop: '4px' }}
                      >
                        View listing →
                      </Link>
                    )}
                  </div>
                </div>
                )}
              </div>

              {/* Desktop: vertical center divider with swap icon */}
              <div className="accepted-trade-swap-desktop" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flexShrink: 0, padding: '0 1rem', position: 'relative' }}>
                <div style={{ position: 'absolute', top: 0, bottom: 0, left: '50%', width: '1px', backgroundColor: '#e5e7eb' }}></div>
                <span style={{ fontSize: '1.25rem', color: '#9ca3af', position: 'relative', backgroundColor: 'white', padding: '4px 0' }}>⇄</span>
              </div>
              {/* Mobile center divider */}
              <div className="accepted-trade-mobile-divider" style={{ display: 'none', flexShrink: 0, width: '1px', backgroundColor: '#e5e7eb', margin: '0 0.5rem' }}></div>

              {/* Their Pedal(s) */}
              <div className={trade.myPedals && trade.myPedals.length > 1 ? 'accepted-single-pedal-col' : ''} style={{ flex: 1 }}>
                <div className="accepted-mobile-username" style={{ display: 'none', fontSize: '0.8rem', color: '#6b7280', fontFamily: 'Nunito', marginBottom: '6px' }}>
                  {usernames[isFromUser ? trade.toUserId : trade.fromUserId] || 'Loading...'}
                </div>
                {trade.theirPedals && trade.theirPedals.length > 1 ? (
                  <div>
                    <div className="accepted-multi-container" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {trade.theirPedals.map((pedal, idx) => (
                        <Link
                          key={pedal?.id || idx}
                          className="accepted-multi-link"
                          to={`/archived-listing/trade/${trade.id}/their${pedal?.id ? `/${pedal.id}` : ''}`}
                          style={{ display: 'flex', gap: '10px', alignItems: idx === 0 ? 'flex-start' : 'center', textDecoration: 'none', color: 'inherit' }}
                        >
                          {(pedal?.images?.top || placeholderFor(pedal || {}).url) ? (
                            <img src={pedal?.images?.top || placeholderFor(pedal || {}).url} alt={`${pedal.make} ${pedal.model}`}
                              className="accepted-multi-image"
                              style={{ width: '64px', height: '64px', objectFit: 'cover', borderRadius: '6px', backgroundColor: '#f3f4f6', flexShrink: 0 }} />
                          ) : (
                            <div className="accepted-multi-image" style={{ width: '64px', height: '64px', backgroundColor: '#f3f4f6', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                              <span style={{ fontSize: '0.6rem', color: '#9ca3af' }}>No Image</span>
                            </div>
                          )}
                          <div className="accepted-multi-text" style={{ textAlign: 'left' }}>
                            {idx === 0 && (
                              <div className="accepted-desktop-username" style={{ fontSize: '0.8rem', color: '#6b7280', fontFamily: 'Nunito', marginBottom: '2px' }}>
                                {usernames[isFromUser ? trade.toUserId : trade.fromUserId] || 'Loading...'}
                              </div>
                            )}
                            <span style={{ fontWeight: '700', fontSize: '0.85rem', color: '#5A9ECB', fontFamily: 'Nunito' }}>{pedal?.make}</span>{' '}
                            <span style={{ fontSize: '0.85rem', color: '#5A9ECB', fontFamily: 'Nunito' }}>{pedal?.model}</span>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                ) : (
                <div className="accepted-pedal-inner" style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                  {(trade.theirPedal?.images?.top || placeholderFor(trade.theirPedal || {}).url) ? (
                    <img
                      src={trade.theirPedal?.images?.top || placeholderFor(trade.theirPedal || {}).url}
                      alt='their pedal'
                      className="accepted-trade-image"
                      style={{ width: '120px', height: '120px', objectFit: 'cover', borderRadius: '8px', backgroundColor: '#f3f4f6', flexShrink: 0 }}
                    />
                  ) : (
                    <div className="accepted-trade-image" style={{ width: '120px', height: '120px', backgroundColor: '#f3f4f6', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <span style={{ fontSize: '0.7rem', color: '#9ca3af' }}>No Image</span>
                    </div>
                  )}
                  <div style={{ textAlign: 'left' }}>
                    <div className="accepted-desktop-username" style={{ fontSize: '0.8rem', color: '#6b7280', fontFamily: 'Nunito', marginBottom: '2px' }}>
                      {usernames[isFromUser ? trade.toUserId : trade.fromUserId] || 'Loading...'}
                    </div>
                    <div style={{ fontWeight: '700', fontSize: '0.95rem', color: '#1f2937', fontFamily: 'Nunito' }}>{trade.theirPedal?.make}</div>
                    <div style={{ fontSize: '0.85rem', color: '#6b7280', fontFamily: 'Nunito' }}>{trade.theirPedal?.model}</div>
                    <div style={{ fontSize: '0.8rem', color: '#9ca3af', fontFamily: 'Nunito', marginTop: '2px' }}>
                      {trade.theirPedal?.condition} · ${trade.theirPedal?.price}
                    </div>
                    {trade.theirPedal?.id && (
                      <Link
                        to={`/archived-listing/trade/${trade.id}/their`}
                        style={{ fontSize: '0.8rem', color: '#5A9ECB', textDecoration: 'none', fontFamily: 'Nunito', display: 'inline-block', marginTop: '4px' }}
                      >
                        View listing →
                      </Link>
                    )}
                  </div>
                </div>
                )}
              </div>
            </div>

            {/* Bottom section: Buttons */}
            <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '0.75rem' }}>
              <div className="accepted-trade-buttons" style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                <Link
                  to={`/trade-progress/${trade.id}`}
                  style={{
                    padding: '8px 20px',
                    borderRadius: '9999px',
                    border: '1px solid #d1d5db',
                    backgroundColor: 'white',
                    color: '#374151',
                    fontWeight: '700',
                    fontSize: '0.9rem',
                    fontFamily: 'Nunito',
                    textDecoration: 'none',
                    display: 'inline-block',
                    whiteSpace: 'nowrap'
                  }}
                >
                  View progress
                </Link>
                {!userPaid && (
                  <Link
                    className="accepted-payment-button"
                    to={`/trade-confirm/${trade.id}`}
                    style={{
                      padding: '8px 20px',
                      borderRadius: '9999px',
                      border: '1px solid #C9A227',
                      backgroundColor: '#C9A227',
                      color: 'white',
                      fontWeight: '700',
                      fontSize: '0.9rem',
                      fontFamily: 'Nunito',
                      textDecoration: 'none',
                      display: 'inline-block',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    Complete Payment →
                  </Link>
                )}
                <Link
                  to={`/trade-messages/${trade.id}`}
                  style={{
                    padding: '8px 20px',
                    borderRadius: '9999px',
                    border: '1px solid #d1d5db',
                    backgroundColor: 'white',
                    color: '#374151',
                    fontWeight: '700',
                    fontSize: '0.9rem',
                    fontFamily: 'Nunito',
                    textDecoration: 'none',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.25rem',
                    whiteSpace: 'nowrap'
                  }}
                >
                  Messages
                  {unreadCounts[trade.id] > 0 && (
                    <span style={{
                      background: '#e74c3c',
                      color: 'white',
                      borderRadius: '10px',
                      padding: '2px 6px',
                      fontSize: '11px',
                      fontWeight: 'bold'
                    }}>
                      {unreadCounts[trade.id]}
                    </span>
                  )}
                </Link>
              </div>
            </div>
          </div>
        )})}
      </div>
    )}

    {/* Fixed gap below content */}
    <div style={{ height: '200px' }}></div>
    </div>
    </div>
    
    {/* Footer */}
    <Footer />

    {/* Back to Top Button */}
    {showBackToTop && (
      <button
        onClick={scrollToTop}
        className="back-to-top-btn"
        style={{
          position: 'fixed',
          bottom: window.innerWidth <= 768 ? '90px' : '30px',
          right: '30px',
          backgroundColor: '#5A9ECB',
          color: 'white',
          border: 'none',
          borderRadius: '50%',
          width: '36px',
          height: '36px',
          fontSize: '16px',
          fontWeight: 'bold',
          cursor: 'pointer',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          zIndex: 1000,
          transition: 'all 0.3s ease',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: 'system-ui, -apple-system, sans-serif'
        }}
        onMouseEnter={(e) => {
          e.target.style.backgroundColor = '#4A8AB0';
          e.target.style.transform = 'scale(1.1)';
        }}
        onMouseLeave={(e) => {
          e.target.style.backgroundColor = '#5A9ECB';
          e.target.style.transform = 'scale(1)';
        }}
        aria-label="Back to top"
      >
        ⌃
      </button>
    )}
  </div>
);





};

export default AcceptedTrades;
